import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { PublicBooking, PublicEventType } from '../../../types'
import EventInfo from './EventInfo'
import MonthCalendar from './MonthCalendar'
import TimeSlots, { useHour12 } from './TimeSlots'
import BookingForm, { BookingFormValues } from './BookingForm'
import BookingSuccess from './BookingSuccess'
import useSlots, { FetchSlots } from './useSlots'
import { browserTimezone, dateKey, formatDateLong, formatTime, fromDateKey } from './format'
import { CreateBookingResult } from '../../../booking/api'

const WIDE_MIN_WIDTH = 768

// Single-layout switch: narrow until measured, wide once the root is >= 48rem.
function useIsWide(ref: React.RefObject<HTMLDivElement>) {
  const [wide, setWide] = React.useState(false)
  React.useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const update = () => setWide(el.getBoundingClientRect().width >= WIDE_MIN_WIDTH)
    update()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  return wide
}

const pad = (n: number) => String(n).padStart(2, '0')

type Step = 'select-date' | 'select-time' | 'form' | 'success'

interface IProps {
  eventType: PublicEventType
  mode: 'live' | 'preview'
  fetchSlots: FetchSlots
  submitBooking?: (body: BookingFormValues & { start: string, duration: number, timezone: string }) => Promise<CreateBookingResult>
  onBooked?: (booking: PublicBooking) => void
  previewValues?: Partial<BookingFormValues>
  // Bump to force a slots refetch (e.g. after availability overrides change in the editor)
  refreshKey?: number | string
  // Drops the outer border/radius (and shrinks padding) when rendered inside an iframe/widget
  embedded?: boolean
  className?: string
}

export default function Booker({ eventType, mode, fetchSlots, submitBooking, onBooked, previewValues, refreshKey, embedded, className }: IProps) {
  const { t, i18n } = useTranslation()
  const [timezone, setTimezone] = React.useState(browserTimezone())
  const [duration, setDuration] = React.useState(eventType.duration)
  const [month, setMonth] = React.useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1) })
  const [selectedDate, setSelectedDate] = React.useState<string | null>(null)
  const [selectedSlot, setSelectedSlot] = React.useState<string | null>(null)
  const [step, setStep] = React.useState<Step>('select-date')
  const [hour12, setHour12] = useHour12()
  const [submitting, setSubmitting] = React.useState(false)
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({})
  const [generalError, setGeneralError] = React.useState<string | null>(null)
  const rootRef = React.useRef<HTMLDivElement>(null)
  const wide = useIsWide(rootRef)
  const [draft, setDraft] = React.useState<BookingFormValues | null>(null)
  const [booking, setBooking] = React.useState<PublicBooking | null>(null)

  React.useEffect(() => { setDuration(eventType.duration) }, [eventType.duration])

  const { slots: rawSlots, loading, error, reload } = useSlots(fetchSlots, month, timezone, duration, refreshKey)

  // The fetch window is padded past the month edges; keys before the visible month are dropped,
  // trailing next-month keys are kept for the days the calendar grid shows after the month ends
  const monthStartKey = `${month.getFullYear()}-${pad(month.getMonth() + 1)}-01`
  const slots = React.useMemo(() => {
    const result: Record<string, string[]> = {}
    Object.keys(rawSlots).forEach(key => { if (key >= monthStartKey) result[key] = rawSlots[key] })
    return result
  }, [rawSlots, monthStartKey])

  // Auto-select first available date in the visible month (wide layout convenience)
  React.useEffect(() => {
    if (selectedDate && slots[selectedDate]?.length) return
    const first = Object.keys(slots).sort().find(key => slots[key]?.length)
    setSelectedDate(first || null)
  }, [slots])

  const daySlots = selectedDate ? slots[selectedDate] || [] : []

  const handleSelectDate = (date: Date) => {
    setSelectedDate(dateKey(date))
    setStep('select-time')
  }

  const endTime = selectedSlot ? new Date(new Date(selectedSlot).getTime() + duration * 60000).toISOString() : null

  const handleSelectSlot = (iso: string) => {
    setSelectedSlot(iso)
    setFieldErrors({})
    setGeneralError(null)
    setStep('form')
  }

  const handleSubmit = async (values: BookingFormValues) => {
    if (mode === 'preview' || !submitBooking || !selectedSlot) return
    setSubmitting(true)
    setFieldErrors({})
    setGeneralError(null)
    let result: CreateBookingResult
    try {
      result = await submitBooking({ ...values, start: selectedSlot, duration, timezone })
    } catch {
      result = { ok: false, status: 0, error: 'network' }
    } finally {
      setSubmitting(false)
    }

    if (result.ok === true) {
      setBooking(result.booking)
      setStep('success')
      onBooked?.(result.booking)
      return
    }
    if (result.error === 'slot_unavailable') {
      toast.error(t('Scheduling::This slot was just taken. Please pick another time.'))
      setSelectedSlot(null)
      setStep('select-time')
      reload()
    } else if (result.error === 'booker_limit_reached') {
      setGeneralError(t('Scheduling::You have reached the maximum number of upcoming bookings for this event.'))
    } else if (result.error === 'invalid') {
      const details = result.details || {}
      setFieldErrors(details)
      if (!['name', 'email', 'guests', 'notes', 'location_value'].some(key => details[key])) {
        setGeneralError(t('Scheduling::Please check your details and try again.'))
      }
    } else if (result.status === 429) {
      setGeneralError(t('Scheduling::Too many requests. Please try again in a few minutes.'))
    } else {
      setGeneralError(t('Scheduling::Something went wrong. Please try again.'))
    }
  }

  if (step === 'success' && booking) {
    return (
      <div className={cn('mx-auto w-full max-w-2xl', className)}>
        <BookingSuccess booking={booking} timezone={timezone} hour12={hour12} embedded={embedded} />
      </div>
    )
  }

  const selectedDateObj = selectedDate ? fromDateKey(selectedDate) : undefined
  const dayHeading = selectedDateObj && (
    <>
      {new Intl.DateTimeFormat(i18n.language, { weekday: 'short' }).format(selectedDateObj)}{' '}
      <span className='text-sm font-medium text-muted-foreground'>{selectedDateObj.getDate()}</span>
    </>
  )

  const info = (compact: boolean) => (
    <EventInfo
      eventType={eventType} duration={duration} durations={eventType.durations}
      onDurationChange={(d) => { setDuration(d); setSelectedSlot(null); if (step === 'form') setStep('select-time') }}
      timezone={timezone} onTimezoneChange={setTimezone}
      compact={compact}
      when={step === 'form' && selectedSlot && endTime && (
        <>
          <div>{formatDateLong(selectedSlot, timezone, i18n.language)}</div>
          <div>{formatTime(selectedSlot, timezone, hour12, i18n.language)} – {formatTime(endTime, timezone, hour12, i18n.language)}</div>
        </>
      )}
    />
  )

  const calendar = (
    <>
      <MonthCalendar
        month={month} onMonthChange={(m) => { setMonth(m); setSelectedDate(null) }}
        selected={selectedDateObj} onSelect={handleSelectDate} slots={slots} loading={loading}
      />
      {error && (
        <div className='mt-2 flex items-center gap-2 text-sm text-destructive'>
          {t('Scheduling::Could not load availability.')}
          <Button variant='link' size='sm' onClick={reload}>{t('Scheduling::Retry')}</Button>
        </div>
      )}
    </>
  )

  const timeSlots = (
    <TimeSlots heading={dayHeading} slots={daySlots} timezone={timezone} hour12={hour12} onHour12Change={setHour12} onSelect={handleSelectSlot} loading={loading} wide={wide} />
  )

  const form = (
    <BookingForm
      locations={eventType.locations}
      initialValues={draft || (mode === 'preview' ? previewValues : undefined)}
      onValuesChange={setDraft}
      disabled={mode === 'preview'}
      submitting={submitting}
      errors={fieldErrors}
      generalError={generalError}
      onBack={() => setStep('select-time')}
      onSubmit={handleSubmit}
    />
  )

  // Layout mirrors cal.com's month view: 280 / 480 / 280px columns, narrowing to 340 / 420px on the form step.
  // The date/time steps keep a fixed 488px card height (the slot list scrolls within it); the form step uses it as a
  // minimum so the card grows to fit the form instead of clipping it.
  return (
    <div ref={rootRef} className={cn('@container w-full', className)}>
      {wide ? (
        <div className={cn(
          'mx-auto grid w-full bg-background transition-[max-width] duration-300',
          !embedded && 'rounded-md border',
          step === 'form' ? 'min-h-122 max-w-190 grid-cols-[minmax(0,21.25rem)_minmax(0,1fr)]' : 'h-122 max-w-260 grid-cols-[minmax(0,17.5rem)_minmax(0,1fr)_minmax(0,17.5rem)]',
        )}>
          <div className='min-h-0 min-w-0 overflow-y-auto p-6'>{info(false)}</div>
          {step === 'form' ? (
            <div className='min-w-0 border-l p-6'>{form}</div>
          ) : (
            <>
              <div className='min-h-0 min-w-0 overflow-y-auto border-l px-5 py-3'>{calendar}</div>
              {/* Absolutely positioned so the slot list scrolls within the card height instead of stretching the row */}
              <div className='relative min-w-0 border-l'>
                <div className='absolute inset-0 px-5 pt-3'>{selectedDate ? timeSlots : null}</div>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className={cn('flex flex-col bg-background', !embedded && 'rounded-md border')}>
          <div className='p-4'>{info(true)}</div>
          {step === 'form' ? (
            <div className='border-t p-4'>{form}</div>
          ) : (
            <>
              <div className='mx-auto w-full max-w-120 border-t px-4 py-3'>{calendar}</div>
              {selectedDate && <div className='border-t p-4'>{timeSlots}</div>}
            </>
          )}
        </div>
      )}
    </div>
  )
}
